import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismButtonGroup,
  PrismInput,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, eq, not, or } from 'ember-truth-helpers';
import add from 'lumen/helpers/add';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import type { TagType } from 'lumen/models/custom-tag-definition';
import type CustomTagListOption from 'lumen/models/custom-tag-list-option';
import {
  MAX_CUSTOM_TAG_DEFINITION_OPTIONS,
  MAX_NAME_LENGTH,
  TAG_TYPE_TRANSLATIONS,
  TAG_TYPES,
} from 'lumen/utils/constants/property-tags';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface Signature {
  Args: {
    definition: CustomTagDefinition;
    isNameUnique: (name: string) => boolean;
    onClose: () => void;
    onSuccess: () => void;
  };
}

export default class SettingsCustomPropertyTagsModalsModalDefinition extends Component<Signature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked hasSubmitted = false;
  @tracked name: string = this.args.definition.name ?? '';
  @tracked tagType: TagType = this.args.definition.tagType ?? 'list';

  saveDefinitionTask = task(async (): Promise<void> => {
    this.hasSubmitted = true;
    for (const opt of this.visibleOptions) {
      opt.isTouched = true;
    }
    if (!this.isFormValid) {
      return;
    }
    try {
      if (this.args.definition.isNew) {
        await this.createDefinition();
        this.toast.success(
          this.intl.t(
            'settings.property_tags.custom_tags.form.toast.tag_created',
          ),
          { showIcon: true },
        );
      } else {
        await this.updateDefinition();
        this.toast.success(
          this.intl.t(
            'settings.property_tags.custom_tags.form.toast.tag_updated',
          ),
          { showIcon: true },
        );
      }
      this.args.onSuccess();
    } catch (error) {
      this.errorTracking.captureException(error, {
        feature: 'custom-property-tags.save-definition',
      });
      void this.alerting.showErrorAlert();
      this.args.definition.rollbackAttributes();
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get canAddOption(): boolean {
    return this.visibleOptions.length < MAX_CUSTOM_TAG_DEFINITION_OPTIONS;
  }

  get deletedOptionsWithSubscriptions(): CustomTagListOption[] {
    return this.args.definition.options.filter(
      (opt) => opt.isDeleted && (opt.subscriberCount ?? 0) > 0,
    );
  }

  get hasNameError(): boolean {
    return this.nameErrorMsg !== null;
  }

  get hasNoOptions(): boolean {
    return this.tagType === 'list' && this.visibleOptions.length === 0;
  }

  get hasOptionErrors(): boolean {
    return (
      this.tagType === 'list' && this.visibleOptions.some((opt) => opt.hasError)
    );
  }

  get isFormValid(): boolean {
    return (
      this.isNameValid &&
      this.nameErrorMsg === null &&
      !this.hasNoOptions &&
      !this.hasOptionErrors
    );
  }

  get isNameValid(): boolean {
    return this.name.trim().length > 0;
  }

  get nameErrorMessage(): string | undefined {
    return this.nameErrorMsg ?? undefined;
  }

  get nameErrorMsg(): string | null {
    const trimmed = this.name.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      return this.intl.t(
        'settings.property_tags.custom_tags.form.validations.name_too_long',
      );
    }
    if (!this.args.isNameUnique(this.name)) {
      return this.intl.t(
        'settings.property_tags.custom_tags.form.validations.name_taken',
      );
    }
    return null;
  }

  get saveLabel(): string {
    return this.args.definition.isNew
      ? this.intl.t('settings.property_tags.custom_tags.create_tag')
      : this.intl.t('settings.property_tags.custom_tags.form.save_tag');
  }

  get subscriptionsBannerMessage(): string | null {
    const opts = this.deletedOptionsWithSubscriptions;
    if (opts.length === 0) {
      return null;
    }

    if (opts.length === 1 && opts[0]) {
      return this.intl.t(
        'settings.property_tags.custom_tags.form.option_delete_alert',
        { title: opts[0].label, count: opts[0].subscriberCount },
      );
    }

    const totalHotels = opts.reduce(
      (sum, o) => sum + (o.subscriberCount ?? 0),
      0,
    );
    return this.intl.t(
      'settings.property_tags.custom_tags.form.options_delete_alert',
      { num: opts.length, count: totalHotels },
    );
  }

  get tagTypeOptions(): { label: string; value: TagType }[] {
    return TAG_TYPES.map((type) => ({
      label: this.intl.t(TAG_TYPE_TRANSLATIONS[type]),
      value: type,
    }));
  }

  get title(): string {
    return this.args.definition.isNew
      ? this.intl.t('settings.property_tags.custom_tags.form.create_new_tag')
      : this.intl.t('settings.property_tags.custom_tags.form.edit_tag');
  }

  get visibleOptions(): CustomTagListOption[] {
    return this.args.definition.options.filter((opt) => !opt.isDeleted);
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    if (args.definition.isNew) {
      this.store.createRecord<CustomTagListOption>('custom-tag-list-option', {
        definition: args.definition,
      });
    }
  }

  private async createDefinition(): Promise<void> {
    const { definition } = this.args;
    definition.name = this.name.trim();
    definition.tagType = this.tagType;

    if (this.tagType !== 'list') {
      // Unload zombie options created by default — none should be sent to the server
      // this can happen if you switch between tag types after creating list options
      for (const opt of definition.options) {
        opt.unloadRecord();
      }
    }

    await definition.save();

    // Unload remaining temp records (no id) — EmbeddedRecordsMixin has pushed canonical ones
    // On create we move every option label to a list.. the "zombie" records has to be cleared
    for (const opt of definition.options.filter((o) => !o.id)) {
      opt.unloadRecord();
    }
  }

  private async saveOptionsIndividually(): Promise<void> {
    const definitionId = this.args.definition.id;

    for (const opt of [...this.args.definition.options]) {
      if (opt.isDeleted) {
        await opt.destroyRecord({ adapterOptions: { definitionId } });
      } else if (!opt.id) {
        await opt.save({ adapterOptions: { definitionId } });
      } else if (opt.hasDirtyAttributes) {
        await opt.save({ adapterOptions: { definitionId, usePatch: true } });
      }
    }
  }

  private async updateDefinition(): Promise<void> {
    const { definition } = this.args;
    const nameTrimmed = this.name.trim();

    if (nameTrimmed !== definition.name) {
      definition.name = nameTrimmed;
      await definition.save({ adapterOptions: { usePatch: true } });
    }

    if (this.tagType === 'list') {
      await this.saveOptionsIndividually();
    }
  }

  @action
  addOption(): void {
    if (!this.canAddOption) {
      return;
    }
    this.store.createRecord<CustomTagListOption>('custom-tag-list-option', {
      definition: this.args.definition,
    });
  }

  @action
  onNameInput(event: Event): void {
    this.name = (event.target as HTMLInputElement).value;
  }

  @action
  onTagTypeChange(value: string | string[]): void {
    this.tagType = (Array.isArray(value) ? value[0] : value) as TagType;
  }

  @action
  removeOption(opt: CustomTagListOption): void {
    if (opt.id) {
      opt.deleteRecord();
    } else {
      opt.unloadRecord();
    }
  }

  @action
  undoRemoveOption(): void {
    this.deletedOptionsWithSubscriptions.forEach((opt) =>
      opt.rollbackAttributes(),
    );
  }

  @action
  updateOptionLabel(opt: CustomTagListOption, event: Event): void {
    opt.label = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.saveDefinitionTask.isRunning}}
      @onClose={{@onClose}}
      @title={{this.title}}
      data-test-modal="definition"
    >
      <:default>
        <div class="modal--padded flex flex-col gap-400">
          <PrismInput
            @errorMessage={{if this.hasSubmitted this.nameErrorMessage}}
            @hasErrors={{and this.hasSubmitted this.hasNameError}}
            @isTouched={{this.hasSubmitted}}
            @label={{t "settings.property_tags.custom_tags.form.name"}}
            @maxLength={{MAX_NAME_LENGTH}}
            @onInput={{this.onNameInput}}
            @placeholder={{t
              "settings.property_tags.custom_tags.form.definition_name_placeholder"
            }}
            @required={{true}}
            @requiredErrorMessage={{t
              "settings.property_tags.custom_tags.form.validations.name_required"
            }}
            @value={{this.name}}
            data-test-input="name"
          />
          {{#if @definition.isNew}}
            <div class="flex flex-col gap-200">
              <span class="text-200--bold">{{t
                  "settings.property_tags.custom_tags.tag_type"
                }}</span>
              <PrismButtonGroup
                @onChange={{this.onTagTypeChange}}
                @value={{this.tagType}}
                data-test-input="tag-type"
                as |ButtonGroup|
              >
                {{#each this.tagTypeOptions as |option|}}
                  <ButtonGroup.button
                    @label={{option.label}}
                    @value={{option.value}}
                  />
                {{/each}}
              </PrismButtonGroup>
            </div>
          {{/if}}
          {{#if this.subscriptionsBannerMessage}}
            <PrismBanner
              @bannerMessage={{this.subscriptionsBannerMessage}}
              @bannerStyle="info"
              @hasCta={{true}}
              @isOneLine={{true}}
              @primaryAction={{hash
                action=this.undoRemoveOption
                label=(if
                  (eq this.deletedOptionsWithSubscriptions.length 1)
                  (t "general.undo")
                  (t "general.undo_all")
                )
              }}
              data-test-id="banner-subscriber-warning"
            />
          {{/if}}
          <div class="flex flex-col gap-200" data-test-section="values">
            <div class="flex items-center justify-between">
              <span class="text-200--bold">{{t
                  "settings.property_tags.custom_tags.values"
                }}</span>
              {{#if (eq this.tagType "list")}}
                <span
                  class="text-200--regular text-subdued"
                >{{this.visibleOptions.length}}/{{MAX_CUSTOM_TAG_DEFINITION_OPTIONS}}</span>
              {{/if}}
            </div>
            <div
              class="rounded-200 border-neutral-default bg-neutral-subdued flex flex-col gap-400 border p-400"
            >
              {{#if (eq this.tagType "user")}}
                <p class="text-300--regular text-neutral-default">{{t
                    "settings.property_tags.custom_tags.form.tag_type.users_description"
                  }}</p>
              {{/if}}
              {{#if (eq this.tagType "text")}}
                <p class="text-300--regular text-neutral-default">{{t
                    "settings.property_tags.custom_tags.form.tag_type.text_description"
                  }}</p>
              {{/if}}
              {{#if (eq this.tagType "list")}}
                {{#each this.visibleOptions key="@identity" as |opt index|}}
                  <div
                    class="flex items-center gap-300"
                    data-test-option={{index}}
                  >
                    <div class="w-400">
                      <span class="text-300--regular text-subdued">{{add
                          index
                          1
                        }}.</span>
                    </div>
                    <div class="flex flex-1">
                      <PrismInput
                        @errorMessage={{if this.hasSubmitted opt.errorMessage}}
                        @hasErrors={{and this.hasSubmitted opt.hasError}}
                        @isTouched={{opt.isTouched}}
                        @maxLength={{MAX_NAME_LENGTH}}
                        @onInput={{fn this.updateOptionLabel opt}}
                        @placeholder={{t
                          "settings.property_tags.custom_tags.form.option_placeholder"
                        }}
                        @removePadding={{true}}
                        @required={{true}}
                        @requiredErrorMessage={{t
                          "settings.property_tags.custom_tags.form.validations.name_required"
                        }}
                        @value={{opt.label}}
                        data-test-input="option-label"
                      />
                    </div>
                    <div>
                      <PrismButton
                        @buttonType="ghost"
                        @icon="bin"
                        @isDisabled={{or
                          (eq this.visibleOptions.length 1)
                          this.saveDefinitionTask.isRunning
                        }}
                        @onClick={{fn this.removeOption opt}}
                        @size="small"
                        data-test-button="remove-option"
                      />
                    </div>
                  </div>
                {{/each}}
                <div class="ml-600">
                  <PrismButton
                    @buttonType="secondary"
                    @iconBefore="plus"
                    @isDisabled={{or
                      (not this.canAddOption)
                      this.saveDefinitionTask.isRunning
                    }}
                    @label={{t
                      "settings.property_tags.custom_tags.form.add_option"
                    }}
                    @onClick={{this.addOption}}
                    @size="small"
                    class="ml-100"
                    data-test-button="add-option"
                  />
                </div>
              {{/if}}
            </div>
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-200 px-500">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.saveDefinitionTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-button="cancel"
          />
          <PrismButton
            @isDisabled={{this.saveDefinitionTask.isRunning}}
            @isLoading={{this.saveDefinitionTask.isRunning}}
            @label={{this.saveLabel}}
            @onClick={{this.saveDefinitionTask.perform}}
            data-test-button="save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
